import { Injectable, OnDestroy } from '@angular/core';
import { ReplaySubject, Subscription, timer, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { DataService } from './data.service';
import { environment } from '../../../environments/environment';

export interface PendingPaymentLink {
  Id?: number;
  IDEnlace?: number;
  Concepto: string;
  Importe: string;
  Localizador: string;
  FechaCaducidad: string;
  Observaciones?: string;
  ObservacionesServivuelo?: string;
}

const POLLING_INTERVAL_MS = 5000;
const SESSION_KNOWN_IDS_KEY = 'paymentLinksKnownIds';

@Injectable({
  providedIn: 'root',
})
export class PaymentLinksPollingService implements OnDestroy {
  pendingLinksChanged = new ReplaySubject<PendingPaymentLink[]>(1);

  private pollingSubscription: Subscription | null = null;
  private knownLinkIds = new Set<number>();
  private isFirstLoad = true;

  constructor(private dataService: DataService) {}

  private getLinkId(link: PendingPaymentLink): number | undefined {
    return link.IDEnlace ?? link.Id;
  }

  private debug(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  private loadKnownIdsFromSession(): void {
    try {
      const stored = sessionStorage.getItem(SESSION_KNOWN_IDS_KEY);
      if (stored) {
        const ids: number[] = JSON.parse(stored);
        ids.forEach(id => {
          if (id != null) {
            this.knownLinkIds.add(id);
          }
        });
      }
    } catch {
      sessionStorage.removeItem(SESSION_KNOWN_IDS_KEY);
    }
  }

  private saveKnownIdsToSession(): void {
    sessionStorage.setItem(SESSION_KNOWN_IDS_KEY, JSON.stringify([...this.knownLinkIds]));
  }

  startPolling(): void {
    if (this.pollingSubscription) {
      return;
    }

    this.isFirstLoad = true;
    this.knownLinkIds.clear();
    this.loadKnownIdsFromSession();

    this.pollingSubscription = timer(0, POLLING_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.dataService.getData('agencias/listadopasarelapagoenlace').pipe(
            catchError(error => {
              console.error('Error polling payment links:', error);
              return of(null);
            })
          )
        )
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        const pendingLinks: PendingPaymentLink[] = this.extractLinks(response);

        this.debug('[PaymentLinksPolling] Response:', response);
        this.debug('[PaymentLinksPolling] Parsed links:', pendingLinks.length, 'isFirstLoad:', this.isFirstLoad);

        if (pendingLinks.length === 0) {
          this.isFirstLoad = false;
          return;
        }

        if (this.isFirstLoad) {
          this.isFirstLoad = false;
          const alreadyKnown = this.knownLinkIds.size > 0;

          if (!alreadyKnown) {
            // Primera carga real (sin sesión previa): mostrar popup con todos los links
            pendingLinks.forEach(link => {
              const id = this.getLinkId(link);
              if (id != null) {
                this.knownLinkIds.add(id);
              }
            });
            this.saveKnownIdsToSession();
            this.debug('[PaymentLinksPolling] Initial IDs stored:', [...this.knownLinkIds]);
            this.pendingLinksChanged.next(pendingLinks);
          } else {
            // Refresh dentro de la misma sesión: solo mostrar links nuevos
            const newLinks = pendingLinks.filter(link => {
              const id = this.getLinkId(link);
              return id != null && !this.knownLinkIds.has(id);
            });
            pendingLinks.forEach(link => {
              const id = this.getLinkId(link);
              if (id != null) {
                this.knownLinkIds.add(id);
              }
            });
            this.saveKnownIdsToSession();
            this.debug('[PaymentLinksPolling] Session reload - new links:', newLinks.length);
            if (newLinks.length > 0) {
              this.pendingLinksChanged.next(pendingLinks);
            }
          }
          return;
        }

        const newLinks = pendingLinks.filter(link => {
          const id = this.getLinkId(link);
          return id != null && !this.knownLinkIds.has(id);
        });
        this.debug(
          '[PaymentLinksPolling] New links found:',
          newLinks.length,
          newLinks.map(l => this.getLinkId(l))
        );

        if (newLinks.length > 0) {
          pendingLinks.forEach(link => {
            const id = this.getLinkId(link);
            if (id != null) {
              this.knownLinkIds.add(id);
            }
          });
          this.saveKnownIdsToSession();
          this.pendingLinksChanged.next(pendingLinks);
        }
      });
  }

  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    this.knownLinkIds.clear();
    this.isFirstLoad = true;
    sessionStorage.removeItem(SESSION_KNOWN_IDS_KEY);
    this.pendingLinksChanged.complete();
    this.pendingLinksChanged = new ReplaySubject<PendingPaymentLink[]>(1);
  }

  markAsKnown(linkIds: number[]): void {
    linkIds.forEach(id => {
      if (id != null) {
        this.knownLinkIds.add(id);
      }
    });
    this.saveKnownIdsToSession();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private extractLinks(response: any): PendingPaymentLink[] {
    let links: PendingPaymentLink[] = [];

    if (Array.isArray(response)) {
      links = response;
    } else if (response?.DatosFormateados && Array.isArray(response.DatosFormateados)) {
      links = response.DatosFormateados;
    } else if (typeof response === 'object') {
      const arrayProp = Object.values(response).find(val => Array.isArray(val));
      if (arrayProp) {
        links = arrayProp as PendingPaymentLink[];
      }
    }

    const normalized: PendingPaymentLink[] = links.map(l => ({
      ...l,
      Id: l.Id ?? l.IDEnlace,
      IDEnlace: l.IDEnlace ?? l.Id,
    }));

    return normalized.filter(link => !this.isExpired(link.FechaCaducidad));
  }

  private isExpired(fechaCaducidad: string): boolean {
    if (!fechaCaducidad) {
      return false;
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // Intentar parsear formato dd/MM/yyyy
    const parts = fechaCaducidad.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (parts) {
      const date = new Date(+parts[3], +parts[2] - 1, +parts[1]);
      return date.getTime() < now.getTime();
    }

    // Intentar parsear formato ISO u otros formatos estándar
    const parsed = new Date(fechaCaducidad);
    if (!isNaN(parsed.getTime())) {
      parsed.setHours(0, 0, 0, 0);
      return parsed.getTime() < now.getTime();
    }

    return false;
  }
}
